import React, { useMemo, useState } from 'react'
import { Select, DatePicker, Modal, message } from 'antd'
import { DeleteOutlined, BarChartOutlined, SmileFilled, FrownFilled, PhoneOutlined, StopOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import moment, { Moment } from 'moment'
import { useQuery } from '@tanstack/react-query'
import {
	ITelephoneSurveyTableItem,
	TELEPHONE_SURVEY_CALL_STATUS,
	TELEPHONE_SURVEY_SATISFACTION
} from '../../../reducers/telephoneSurveys/actions'
import { deleteReq, getReq } from '../../../utils/request'
import TelephoneSurveyChartDrawer from './TelephoneSurveyChartDrawer'
import UsersWithoutPhoneDrawer from './UsersWithoutPhoneDrawer'
import { IUserWithoutPhone } from './EditUserPhoneModal'

type Props = {
	data: ITelephoneSurveyTableItem[]
	loading?: boolean
	onDeleted?: (id: number) => void
}

const CARD_STYLE: React.CSSProperties = {
	background: '#fff',
	border: '1px solid #e5e7eb',
	borderRadius: 8,
	boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)'
}

const splitCalledAt = (calledAt?: string | null) => {
	if (!calledAt) return { date: '-', time: '-' }
	const [date, time] = calledAt.split(' ')
	return { date: date || '-', time: time || '-' }
}

const getStatusBadge = (survey: ITelephoneSurveyTableItem, size: 'sm' | 'md' = 'sm') => {
	const baseStyle: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		borderRadius: 999,
		fontWeight: 600,
		whiteSpace: 'nowrap',
		fontSize: size === 'md' ? 13 : 12,
		padding: size === 'md' ? '5px 10px' : '4px 9px'
	}

	if (survey.callStatus === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER) {
		return (
			<span style={{ ...baseStyle, background: '#ffedd5', color: '#c2410c' }}>
				<span>×</span>
				<span>Nezodvihol</span>
			</span>
		)
	}

	if (survey.callStatus === TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER) {
		return (
			<span style={{ ...baseStyle, background: '#dbeafe', color: '#1d4ed8' }}>
				<span>☎</span>
				<span>Zavolať neskôr</span>
			</span>
		)
	}

	if (survey.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED) {
		return (
			<span style={{ ...baseStyle, background: '#fef9c3', color: '#854d0e' }}>
				<SmileFilled style={{ fontSize: size === 'md' ? 14 : 13 }} />
				<span>Spokojný</span>
			</span>
		)
	}

	if (survey.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED) {
		return (
			<span style={{ ...baseStyle, background: '#fee2e2', color: '#991b1b' }}>
				<FrownFilled style={{ fontSize: size === 'md' ? 14 : 13 }} />
				<span>Nespokojný</span>
			</span>
		)
	}

	return null
}

const StatCard = ({
	label,
	value,
	background = '#fff',
	border = '#e5e7eb',
	color = '#1f2937',
	onClick,
	icon
}: {
	label: string
	value: number | React.ReactNode
	background?: string
	border?: string
	color?: string
	onClick?: () => void
	icon?: React.ReactNode
}) => (
	<div
		role={onClick ? 'button' : undefined}
		tabIndex={onClick ? 0 : undefined}
		onClick={onClick}
		onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick() } : undefined}
		style={{
			...CARD_STYLE,
			background,
			borderColor: border,
			padding: '12px 16px',
			minHeight: 68,
			cursor: onClick ? 'pointer' : undefined,
			transition: onClick ? 'box-shadow 0.15s ease, border-color 0.15s ease' : undefined
		}}
		onMouseEnter={onClick ? (e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 14px rgba(15,23,42,0.12)' } : undefined}
		onMouseLeave={onClick ? (e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(15,23,42,0.06)' } : undefined}
	>
		<div style={{ color: '#4b5563', fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
			{icon}
			{label}
		</div>
		<div style={{
			color, fontSize: 22, lineHeight: '26px', fontWeight: 700
		}}>{value}</div>
	</div>
)

const DetailRow = ({ label, value }: { label: string, value?: React.ReactNode }) => (
	<div style={{
		display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 14
	}}>
		<span style={{ color: '#4b5563' }}>{label}:</span>
		<span style={{ color: '#1f2937', fontWeight: 600, textAlign: 'right' }}>{value || '-'}</span>
	</div>
)

const TelephoneSurveyHistoryList = ({
	data,
	loading,
	onDeleted
}: Props) => {
	const [filterOrganization, setFilterOrganization] = useState('Všetky organizácie')
	const [filterSatisfaction, setFilterSatisfaction] = useState('Všetky')
	const [filterEmployee, setFilterEmployee] = useState('Všetci zamestnanci')
	const [filterOperator, setFilterOperator] = useState('Všetci operátori')
	const [filterDateRange, setFilterDateRange] = useState<[Moment | null, Moment | null]>([null, null])
	const [selectedSurvey, setSelectedSurvey] = useState<ITelephoneSurveyTableItem | null>(null)
	const [showChartDrawer, setShowChartDrawer] = useState(false)
	const [showWithoutPhoneDrawer, setShowWithoutPhoneDrawer] = useState(false)

	const usersWithoutPhoneQuery = useQuery<{ users: IUserWithoutPhone[] }>({
		queryKey: ['users-without-phone'],
		queryFn: async () => {
			const res = await getReq('/api/admin/users/without-phone' as any)
			return res.data
		}
	})

	const usersWithoutPhone = usersWithoutPhoneQuery.data?.users || []

	const handleDelete = (survey: ITelephoneSurveyTableItem, e: React.MouseEvent) => {
		e.stopPropagation()
		Modal.confirm({
			title: 'Vymazať prieskum?',
			content: `Naozaj chcete vymazať prieskum z ${survey.calledAt || '-'} pre ${survey.trusteeName || survey.cleanerNames || '-'}? Táto akcia je nevratná.`,
			okText: 'Vymazať',
			okType: 'danger',
			cancelText: 'Zrušiť',
			onOk: async () => {
				try {
					await deleteReq(`/api/admin/telephone-surveys/${survey.id}`)
					message.success('Prieskum bol vymazaný')
					onDeleted?.(survey.id)
				} catch {
					message.error('Nepodarilo sa vymazať prieskum')
				}
			}
		})
	}

	const organizations = useMemo(() => [
		'Všetky organizácie',
		...Array.from(new Set(data.map((survey) => survey.organizationName || '-')))
	], [data])

	const employees = useMemo(() => [
		'Všetci zamestnanci',
		...Array.from(new Set(data.map((survey) => survey.cleanerNames || '-')))
	], [data])

	const operators = useMemo(() => [
		'Všetci operátori',
		...Array.from(new Set(data.map((survey) => survey.operatorName || '-').filter(Boolean)))
	], [data])

	const [dateFrom, dateTo] = filterDateRange

	const filteredSurveys = useMemo(() => data.filter((survey) => {
		if (filterOrganization !== 'Všetky organizácie' && (survey.organizationName || '-') !== filterOrganization) {
			return false
		}
		if (filterEmployee !== 'Všetci zamestnanci' && (survey.cleanerNames || '-') !== filterEmployee) {
			return false
		}
		if (filterOperator !== 'Všetci operátori' && (survey.operatorName || '-') !== filterOperator) {
			return false
		}
		if (filterSatisfaction === 'Spokojní' && survey.satisfaction !== TELEPHONE_SURVEY_SATISFACTION.SATISFIED) {
			return false
		}
		if (filterSatisfaction === 'Nespokojní' && survey.satisfaction !== TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED) {
			return false
		}
		if (filterSatisfaction === 'Nezodvihol' && survey.callStatus !== TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER) {
			return false
		}
		if (survey.calledAt) {
			const surveyDate = moment(survey.calledAt, 'DD.MM.YYYY HH:mm')
			if (dateFrom && surveyDate.isBefore(dateFrom, 'day')) return false
			if (dateTo && surveyDate.isAfter(dateTo, 'day')) return false
		}
		return true
	}), [data, filterEmployee, filterOrganization, filterOperator, filterSatisfaction, dateFrom, dateTo])

	const satisfiedCount = filteredSurveys.filter((survey) => survey.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED).length
	const unsatisfiedCount = filteredSurveys.filter((survey) => survey.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED).length
	const noAnswerCount = filteredSurveys.filter((survey) => survey.callStatus === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER).length

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<div style={{ ...CARD_STYLE, padding: 16 }}>
				<h3 style={{
					margin: '0 0 14px', color: '#374151', fontSize: 16, fontWeight: 700
				}}>Filtre</h3>
				<div style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
					gap: 14
				}}
				>
					<div>
						<label style={{
							display: 'block', marginBottom: 6, color: '#374151', fontSize: 13, fontWeight: 600
						}}>Organizácia</label>
						<Select
							value={filterOrganization}
							onChange={(value) => setFilterOrganization(String(value))}
							style={{ width: '100%' }}
							size="small"
						>
							{organizations.map((organization) => <Select.Option key={organization} value={organization}>{organization}</Select.Option>)}
						</Select>
					</div>
					<div>
						<label style={{
							display: 'block', marginBottom: 6, color: '#374151', fontSize: 13, fontWeight: 600
						}}>Spokojnosť</label>
						<Select
							value={filterSatisfaction}
							onChange={(value) => setFilterSatisfaction(String(value))}
							style={{ width: '100%' }}
							size="small"
						>
							<Select.Option value={'Všetky'}>Všetky</Select.Option>
							<Select.Option value={'Spokojní'}>Spokojní</Select.Option>
							<Select.Option value={'Nespokojní'}>Nespokojní</Select.Option>
							<Select.Option value={'Nezodvihol'}>Nezodvihol</Select.Option>
						</Select>
					</div>
					<div>
						<label style={{
							display: 'block', marginBottom: 6, color: '#374151', fontSize: 13, fontWeight: 600
						}}>Zamestnanec</label>
						<Select
							value={filterEmployee}
							onChange={(value) => setFilterEmployee(String(value))}
							style={{ width: '100%' }}
							size="small"
						>
							{employees.map((employee) => <Select.Option key={employee} value={employee}>{employee}</Select.Option>)}
						</Select>
					</div>
					<div>
						<label style={{
							display: 'block', marginBottom: 6, color: '#374151', fontSize: 13, fontWeight: 600
						}}>Operátor</label>
						<Select
							value={filterOperator}
							onChange={(value) => setFilterOperator(String(value))}
							style={{ width: '100%' }}
							size="small"
						>
							{operators.map((operator) => <Select.Option key={operator} value={operator}>{operator}</Select.Option>)}
						</Select>
					</div>
					<div>
						<label style={{
							display: 'block', marginBottom: 6, color: '#374151', fontSize: 13, fontWeight: 600
						}}>Časové obdobie</label>
						<DatePicker.RangePicker
							size="small"
							style={{ width: '100%' }}
							format="DD.MM.YYYY"
							value={filterDateRange as any}
							onChange={(dates) => setFilterDateRange(dates ? [dates[0], dates[1]] : [null, null])}
							allowClear
							placeholder={['Od', 'Do']}
						/>
					</div>
				</div>
			</div>

			<div style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
				gap: 18
			}}
			>
				<StatCard
					label={'Celkovo prieskumov'}
					value={filteredSurveys.length}
					icon={<PhoneOutlined style={{ color: '#6b7280' }} />}
				/>
				<StatCard
					label={'Spokojní'}
					value={satisfiedCount}
					background={'#fefce8'}
					border={'#fde68a'}
					color={'#a16207'}
					icon={<SmileFilled style={{ color: '#a16207' }} />}
				/>
				<StatCard
					label={'Nespokojní'}
					value={unsatisfiedCount}
					background={'#fff1f2'}
					border={'#fecdd3'}
					color={'#b91c1c'}
					icon={<FrownFilled style={{ color: '#b91c1c' }} />}
				/>
				<StatCard
					label={'Nezodvihol'}
					value={noAnswerCount}
					background={'#fff7ed'}
					border={'#fed7aa'}
					color={'#c2410c'}
					icon={<StopOutlined style={{ color: '#c2410c' }} />}
				/>
				<StatCard
					label={'Zákazníci bez tel. čísla'}
					value={usersWithoutPhone.length}
					background={'#fff1f2'}
					border={'#fecdd3'}
					color={'#b91c1c'}
					icon={<ExclamationCircleOutlined style={{ color: '#dc2626' }} />}
					onClick={() => setShowWithoutPhoneDrawer(true)}
				/>
				<StatCard
					label={'Graf'}
					value={<BarChartOutlined style={{ fontSize: 22, color: '#334155' }} />}
					background={'#f8fafc'}
					border={'#cbd5e1'}
					color={'#334155'}
					onClick={() => setShowChartDrawer(true)}
				/>
			</div>

			<div style={{ ...CARD_STYLE, overflow: 'hidden' }}>
				<div style={{ overflowX: 'auto' }}>
					<table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1060 }}>
						<thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
							<tr>
								{['Dátum a čas', 'Zákazník', 'Adresa', 'Upratovačka', 'Organizácia', 'Hodnotenie', 'Operátor', ''].map((heading) => (
									<th
										key={heading}
										style={{
											padding: '14px 16px',
											color: '#374151',
											textAlign: 'left',
											fontSize: 14,
											fontWeight: 700,
											verticalAlign: 'middle'
										}}
									>
										{heading}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{loading && (
								<tr>
									<td colSpan={7} style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>Načítavam prieskumy...</td>
								</tr>
							)}
							{!loading && filteredSurveys.length === 0 && (
								<tr>
									<td colSpan={8} style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>Žiadne prieskumy podľa vybraných filtrov</td>
								</tr>
							)}
							{!loading && filteredSurveys.map((survey) => {
								const calledAt = splitCalledAt(survey.calledAt)
								return (
									<tr
										key={survey.id}
										onClick={() => setSelectedSurvey(survey)}
										style={{
											borderBottom: '1px solid #e5e7eb',
											cursor: 'pointer',
											transition: 'background 0.16s ease'
										}}
									>
										<td style={{ padding: '14px 16px', color: '#1f2937', fontSize: 14 }}>
											<div>{calledAt.date}</div>
											<div style={{ color: '#6b7280', fontSize: 13 }}>{calledAt.time}</div>
										</td>
										<td style={{ padding: '14px 16px', color: '#1f2937', fontSize: 14 }}>
											<div>{survey.trusteeName || '-'}</div>
											<div style={{ color: '#6b7280', fontSize: 13 }}>{survey.trusteePhone || '-'}</div>
										</td>
										<td style={{ padding: '14px 16px', color: '#4b5563', fontSize: 14 }}>{survey.jobAddresses}</td>
										<td style={{ padding: '14px 16px', color: '#1f2937', fontSize: 14 }}>{survey.cleanerNames}</td>
										<td style={{ padding: '14px 16px', color: '#4b5563', fontSize: 14 }}>{survey.organizationName || '-'}</td>
										<td style={{ padding: '14px 16px' }}>{getStatusBadge(survey)}</td>
										<td style={{ padding: '14px 16px', color: '#4b5563', fontSize: 14 }}>{survey.operatorName}</td>
										<td style={{ padding: '14px 16px', textAlign: 'center' }}>
											<button
												type="button"
												title="Vymazať prieskum"
												onClick={(e) => handleDelete(survey, e)}
												style={{
													border: 'none',
													background: 'transparent',
													cursor: 'pointer',
													color: '#d1d5db',
													padding: '4px 6px',
													borderRadius: 4,
													transition: 'color 0.15s'
												}}
												onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#ef4444' }}
												onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#d1d5db' }}
											>
												<DeleteOutlined style={{ fontSize: 15 }} />
											</button>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>

			{selectedSurvey && (
				<>
					<div
						role={'button'}
						tabIndex={0}
						onClick={() => setSelectedSurvey(null)}
						onKeyDown={() => setSelectedSurvey(null)}
						style={{
							position: 'fixed',
							inset: 0,
							background: 'rgba(0, 0, 0, 0.5)',
							zIndex: 1040
						}}
					/>
					<div style={{
						position: 'fixed',
						top: 0,
						right: 0,
						width: 'min(600px, 100vw)',
						height: '100vh',
						background: '#fff',
						boxShadow: '-12px 0 28px rgba(15, 23, 42, 0.2)',
						zIndex: 1041,
						overflowY: 'auto'
					}}
					>
						<div style={{
							position: 'sticky',
							top: 0,
							background: '#fff',
							borderBottom: '1px solid #e5e7eb',
							padding: 18,
							display: 'flex',
							justifyContent: 'space-between',
							gap: 16
						}}
						>
							<div>
								<h2 style={{
									margin: 0, color: '#1f2937', fontSize: 20, fontWeight: 700
								}}>Detail prieskumu</h2>
								<p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14 }}>
									{splitCalledAt(selectedSurvey.calledAt).date} {splitCalledAt(selectedSurvey.calledAt).time}
								</p>
							</div>
							<button
								type={'button'}
								onClick={() => setSelectedSurvey(null)}
								style={{
									border: 0,
									background: 'transparent',
									color: '#6b7280',
									fontSize: 24,
									lineHeight: '24px',
									cursor: 'pointer'
								}}
							>
								×
							</button>
						</div>
						<div style={{
							padding: 18, display: 'flex', flexDirection: 'column', gap: 14
						}}>
							<div style={{
								background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14
							}}>
								<h3 style={{
									margin: '0 0 10px', color: '#374151', fontSize: 15, fontWeight: 700
								}}>Hodnotenie</h3>
								{getStatusBadge(selectedSurvey, 'md')}
							</div>
							<div style={{
								background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14
							}}>
								<h3 style={{
									margin: '0 0 10px', color: '#374151', fontSize: 15, fontWeight: 700
								}}>Informácie o zákazníkovi</h3>
								<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
									<DetailRow label={'Meno'} value={selectedSurvey.trusteeName} />
									<DetailRow label={'Telefón'} value={selectedSurvey.trusteePhone} />
									<DetailRow label={'Adresa'} value={selectedSurvey.jobAddresses} />
								</div>
							</div>
							<div style={{
								background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14
							}}>
								<h3 style={{
									margin: '0 0 10px', color: '#374151', fontSize: 15, fontWeight: 700
								}}>Upratovačka</h3>
								<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
									<DetailRow label={'Meno'} value={selectedSurvey.cleanerNames} />
									<DetailRow label={'Organizácia'} value={selectedSurvey.organizationName} />
								</div>
							</div>
							{selectedSurvey.note && (
								<div style={{
									background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14
								}}>
									<h3 style={{
										margin: '0 0 10px', color: '#374151', fontSize: 15, fontWeight: 700
									}}>Poznámka z hovoru</h3>
									<p style={{
										margin: 0, color: '#1f2937', whiteSpace: 'pre-wrap', fontSize: 14
									}}>{selectedSurvey.note}</p>
								</div>
							)}
							<div style={{
								background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14
							}}>
								<h3 style={{
									margin: '0 0 10px', color: '#374151', fontSize: 15, fontWeight: 700
								}}>Informácie o prieskume</h3>
								<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
									<DetailRow label={'Operátor'} value={selectedSurvey.operatorName} />
									<DetailRow label={'Dátum'} value={splitCalledAt(selectedSurvey.calledAt).date} />
									<DetailRow label={'Čas'} value={splitCalledAt(selectedSurvey.calledAt).time} />
									<DetailRow label={'ID prieskumu'} value={selectedSurvey.id} />
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			<TelephoneSurveyChartDrawer
				open={showChartDrawer}
				onClose={() => setShowChartDrawer(false)}
				data={filteredSurveys}
			/>
			<UsersWithoutPhoneDrawer
				open={showWithoutPhoneDrawer}
				onClose={() => setShowWithoutPhoneDrawer(false)}
				users={usersWithoutPhone}
				onRefresh={() => usersWithoutPhoneQuery.refetch()}
			/>
		</div>
	)
}

export default TelephoneSurveyHistoryList
